import { Dialog } from '@base-ui/react/dialog'
import { useEffect, useMemo, useRef } from 'react'
import { keyLabels } from '@/copy/keys'
import { paletteCopy } from '@/copy/palette'
import { Combobox, type ComboboxOption } from '../Combobox/Combobox'
import { cx } from '../cx'
import { Icon } from '../Icon/Icon'
import { Kbd } from '../Kbd/Kbd'
import { useOverlayLayer } from '../overlay/escapeStack'
import { overlayClass } from '../overlay/Overlay'
import styles from './CommandPalette.module.css'

// A Combobox inside a modal shell (CommandPalette card; D97). Deviations:
// Escape via the page-wide stack; after a selection, focus stays wherever the
// caller moved it. Tested.

export type CommandPaletteItem = {
  id: string
  label: string
  meta?: string
  shortcut?: readonly string[]
}

export type CommandPaletteGroup = {
  id: string
  heading: string
  items: readonly CommandPaletteItem[]
}

export function CommandPalette({
  open,
  onOpenChange,
  label,
  inputLabel,
  placeholder,
  value,
  onValue,
  groups,
  onSelect,
  emptyMessage,
  pending,
  error,
  onRetry,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  /** The dialog's name. */
  label: string
  /** The combobox's name. */
  inputLabel: string
  placeholder?: string
  value: string
  onValue: (value: string) => void
  /** Already filtered and ranked; the palette does not rank. */
  groups: readonly CommandPaletteGroup[]
  /** The caller runs it and closes. */
  onSelect: (item: CommandPaletteItem) => void
  emptyMessage: string
  pending?: boolean
  error?: string
  onRetry?: () => void
}) {
  const handle = useOverlayLayer(open, (next) => onOpenChange(next))
  const popupRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const selected = useRef(false)
  useEffect(() => {
    if (open) selected.current = false
  }, [open])

  const items = useMemo(() => {
    const byId = new Map<string, CommandPaletteItem>()
    for (const group of groups) {
      for (const item of group.items) byId.set(item.id, item)
    }
    return byId
  }, [groups])

  const options = useMemo<ComboboxOption[]>(
    () =>
      groups.flatMap((g) =>
        g.items.map((i) => ({
          id: i.id,
          label: i.label,
          group: g.heading,
          meta: i.meta,
          shortcut: i.shortcut,
        })),
      ),
    [groups],
  )

  function select(option: ComboboxOption) {
    const item = items.get(option.id)
    if (!item) return
    selected.current = true
    onSelect(item)
  }

  // Escape and the scrim return focus to where it was. After a selection that
  // moved focus (the frame focuses the new route's h1), leave it there.
  function finalFocus() {
    if (!selected.current) return true
    const active = document.activeElement
    return !active || active === document.body
      ? true
      : !!popupRef.current?.contains(active)
  }

  return (
    <Dialog.Root open={open} onOpenChange={handle}>
      <Dialog.Portal>
        <Dialog.Backdrop className={overlayClass.scrim} data-layer="modal" />
        <Dialog.Viewport className={styles.viewport}>
          <Dialog.Popup
            ref={popupRef}
            aria-label={label}
            aria-modal="true"
            initialFocus={inputRef}
            finalFocus={finalFocus}
            className={cx(overlayClass.surface, styles.popup)}
            data-weight="lg"
          >
            <Combobox
              inline
              autoHighlight="always"
              ref={inputRef}
              label={inputLabel}
              listLabel={paletteCopy.resultsLabel}
              placeholder={placeholder}
              value={value}
              onValue={onValue}
              options={options}
              onSelect={select}
              emptyMessage={emptyMessage}
              pending={pending}
              error={error}
              onRetry={onRetry}
              renderField={(props) => (
                <div className={styles.query}>
                  <Icon
                    name="magnifying-glass"
                    size="icon-embedded"
                    className={styles.glass}
                  />
                  <input {...props} className={styles.input} />
                </div>
              )}
            />
            <div className={styles.footer}>
              <span className={styles.hint}>
                <Kbd keys={[keyLabels.common.up + keyLabels.common.down]} />
                {paletteCopy.hints.move}
              </span>
              <span className={styles.hint}>
                <Kbd keys={['enter']} />
                {paletteCopy.hints.open}
              </span>
              <span className={styles.hint}>
                <Kbd keys={['esc']} />
                {paletteCopy.hints.close}
              </span>
              {/* The region stays mounted so updates are announced; it speaks
                  only once results have loaded. While pending the list is
                  aria-busy; on error the alert speaks. */}
              <span role="status" className={styles.count}>
                {pending || error ? null : paletteCopy.count(options.length)}
              </span>
            </div>
          </Dialog.Popup>
        </Dialog.Viewport>
      </Dialog.Portal>
    </Dialog.Root>
  )
}
